import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, ImagePlus } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-") || "new";

const Photo = ({ m, i, set }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/materials/image", fd); set(i, { image: data.path }); toast.success("Photo added. Press Save to publish."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => ref.current?.click()} className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-[#D2E0D8] bg-[#F4F8F5]" data-testid={`material-photo-${i}`}>
        {m.image ? <img src={fileUrl(m.image)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-[#7B968B]" />}
      </button>
      {busy && <span className="text-xs text-[#6B7F74]">Uploading…</span>}
      {m.image && !busy && <button type="button" onClick={() => set(i, { image: "" })} className="text-xs text-[#B4401F]" data-testid={`material-photo-remove-${i}`}>Remove</button>}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid={`material-photo-input-${i}`} />
    </div>
  );
};

export const AdminMaterials = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/materials").then((r) => setRows(r.data.map((m) => ({ image: "", ...m, original: m.name })))).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const set = (i, p) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const del = (i) => { if (window.confirm(`Delete "${rows[i].name || "this material"}"? Old bookings keep the name. Press Save to confirm.`)) setRows((r) => r.filter((_, j) => j !== i)); };
  const save = async () => {
    try {
      await api.put("/admin/materials", rows.map(({ name, price, active, image, original }) => ({ name, price: Number(price) || 0, active, image: image || "", original: original || "" })));
      toast.success("Materials and prices saved. Customers and partners see them now.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-4xl space-y-4">
      <p className="text-sm text-[#4A6358]">Add, rename, re-price or delete materials, and add a photo for each. Customers see these when booking, and partners' apps use the prices to calculate payments. Switch a material off to hide it for now. Old bookings keep the name they were booked with.</p>
      <div className="overflow-x-auto rounded-2xl border border-[#D2E0D8] bg-white">
        <table className="w-full text-sm" data-testid="admin-materials-table">
          <thead className="bg-[#EAEFEA] text-xs uppercase text-[#4A6358]"><tr><th className="px-3 py-3 text-left">Photo</th><th className="px-3 py-3 text-left">Material</th><th className="px-3 py-3 text-left">₹ per kg</th><th className="px-3 py-3 text-left">Shown</th><th /></tr></thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={`${m.original || "new"}-${i}`} className="border-t border-[#EAEFEA]" data-testid={`material-row-${slug(m.original || m.name)}`}>
                <td className="px-3 py-2"><Photo m={m} i={i} set={set} /></td>
                <td className="px-3 py-2"><Input value={m.name} disabled={m.original === "Other"} onChange={(e) => set(i, { name: e.target.value })} placeholder="e.g. Tin sheets" className="h-9 w-48" data-testid={`material-name-input-${i}`} />
                  {m.original && m.name !== m.original && <p className="mt-1 text-[11px] text-[#B4401F]">Renaming from "{m.original}"</p>}</td>
                <td className="px-3 py-2"><Input type="number" min="0" step="0.5" value={m.price} onChange={(e) => set(i, { price: e.target.value })} className="h-9 w-24" data-testid={`material-price-${slug(m.original || m.name)}`} /></td>
                <td className="px-3 py-2"><input type="checkbox" checked={m.active} onChange={(e) => set(i, { active: e.target.checked })} className="h-5 w-5" data-testid={`material-active-${slug(m.original || m.name)}`} /></td>
                <td className="px-3 py-2">{m.original !== "Other" && <button onClick={() => del(i)} className="text-[#B4401F]" aria-label="delete" data-testid={`material-delete-${i}`}><Trash2 className="h-5 w-5" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3">
        <button onClick={() => setRows((r) => [...r, { name: "", price: 0, active: true, image: "", original: "" }])} className="h-11 rounded-xl border-2 border-dashed border-[#2E9E6B] px-5 font-bold text-[#2E9E6B]" data-testid="material-add-btn">+ Add material</button>
        <button onClick={save} className="h-11 rounded-xl bg-[#0D3B2B] px-6 font-bold text-white" data-testid="materials-save-btn">Save materials & prices</button>
      </div>
    </div>
  );
};
